import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { useParams } from 'react-router-dom';
import * as ep from '../api/endpoints';
import { PAYMENT_LABELS, type PaymentMethod, type ReceiptDetail } from '../api/types';
import { useAuth, useMe } from '../app/auth';
import { ErrorBox, Field, Loading, Money, ReceiptBadge } from '../components/ui';
import { formatDate, formatDateTime, formatDocTime, todayTR } from '../lib/dates';
import { apiToInput, apiToKurus, formatTL, kurusToApi, parseUserAmount } from '../lib/money';

/** Orijinal görsel, tutar, açıklama, sahip, belge tarihi-saati, yükleme zamanı, geçmiş (PDF §04). */
export function ReceiptDetailScreen() {
  const { id = '' } = useParams();
  const me = useMe();
  const q = useQuery({ queryKey: ['receipt', id], queryFn: () => ep.receipt(id) });
  const [mode, setMode] = useState<'view' | 'edit' | 'cancel' | 'refund'>('view');
  const r = q.data;
  if (q.error) return <ErrorBox error={q.error} />;
  if (!r) return <Loading lines={6} />;
  const mine = r.owner_id === me.user.id;
  const remaining = apiToKurus(r.amount) - apiToKurus(r.refunded_total);

  return (
    <div className="split">
      <div className="stack">
        <div className="row">
          <h1 style={{ margin: 0 }}>{r.category}</h1>
          <ReceiptBadge status={r.status} />
        </div>
        <div className="card">
          <table className="t">
            <tbody>
              <tr><th>Tutar (KDV dâhil)</th><td><Money value={r.amount} /></td></tr>
              <tr><th>Bu fişin net katkısı</th><td><Money value={r.net_contribution ?? r.amount} /></td></tr>
              <tr><th>Açıklama</th><td>{r.description}</td></tr>
              <tr><th>Sahip</th><td>{r.owner_name}</td></tr>
              <tr><th>Belge tarihi / saati</th><td>{formatDate(r.document_date)} · {formatDocTime(r.document_time, r.time_precision)}</td></tr>
              <tr><th>Satıcı</th><td>{r.vendor_name ?? '—'}{r.vendor_tax_no ? ` · VKN ${r.vendor_tax_no}` : ''}</td></tr>
              {r.document_no && <tr><th>Belge no</th><td>{r.document_no}</td></tr>}
              <tr><th>Ödeme şekli</th><td>{r.payment_method ? PAYMENT_LABELS[r.payment_method] : '—'}</td></tr>
              {r.payment_method === 'CREDIT_SALE' && (
                <tr><th>Veresiye ödeme tarihi</th><td>
                  {formatDate(r.payment_due_date)}{' '}
                  {r.settlement
                    ? <span className="badge ok">Ödendi · {formatDate(r.settlement.paid_on)}</span>
                    : <span className="badge warn">Ödenmedi</span>}
                </td></tr>
              )}
              <tr><th>Sisteme eklenme</th><td>{formatDateTime(r.created_at)}</td></tr>
              {r.refunds.length > 0 && <tr><th>İade edilen</th><td><Money value={r.refunded_total} /></td></tr>}
              {r.cancellation && <tr><th>İptal</th><td>{r.cancellation.reason} · {formatDateTime(r.cancellation.created_at)}</td></tr>}
            </tbody>
          </table>
        </div>
        {mine && mode === 'view' && (
          <div className="row no-print">
            {r.can_edit && <button className="btn" onClick={() => setMode('edit')}>Düzelt</button>}
            {r.can_edit && remaining > 0n && <button className="btn danger" onClick={() => setMode('cancel')}>İptal et</button>}
            {r.status !== 'CANCELLED' && remaining > 0n && <button className="btn" onClick={() => setMode('refund')}>İade ekle</button>}
          </div>
        )}
        {!mine && <p className="small muted">Bu fişi yalnızca sahibi düzenleyebilir, iptal edebilir veya iade ekleyebilir.</p>}
        {r.payment_method === 'CREDIT_SALE' && r.status !== 'CANCELLED' && mode === 'view' && <CreditPanel r={r} mine={mine} />}
        {mode === 'edit' && <EditForm r={r} onDone={() => setMode('view')} />}
        {mode === 'cancel' && <CancelForm r={r} onDone={() => setMode('view')} />}
        {mode === 'refund' && <RefundForm r={r} remaining={remaining} onDone={() => setMode('view')} />}

        <h2>Geçmiş</h2>
        <div className="card scroll-x">
          <table className="t">
            <thead><tr><th>İşlem</th><th>Tarih</th><th className="num">Etki</th></tr></thead>
            <tbody>
              {r.ledger.map((e) => (
                <tr key={e.id}>
                  <td>{{ EXPENSE: 'Gider', ADJUSTMENT: 'Düzeltme', CANCEL: 'İptal', REFUND: 'İade' }[e.type]}</td>
                  <td>{formatDateTime(e.created_at)}</td>
                  <td className="num"><Money value={e.amount} /></td>
                </tr>
              ))}
            </tbody>
          </table>
          {r.revisions.map((v) => (
            <div key={v.revision_no} className="small" style={{ marginTop: 8 }}>
              <strong>Revizyon {v.revision_no}</strong> · {formatDateTime(v.created_at)} — {v.reason}
            </div>
          ))}
        </div>
      </div>
      <div className="stack">
        <h2>Fiş görselleri</h2>
        {r.files.filter((f) => f.role === 'PREVIEW').map((f, i) => {
          const original = r.files.filter((x) => x.role === 'ORIGINAL')[i];
          return (
            <a key={f.id} href={original?.url ?? f.url ?? '#'} target="_blank" rel="noreferrer" title="Orijinali aç (bağlantı 60 sn geçerli)">
              <img className="photo-full" src={f.url ?? ''} alt={`Fiş görseli ${i + 1}`} loading="lazy" />
            </a>
          );
        })}
        {!r.files.length && <p className="muted">Görsel yok.</p>}
      </div>
    </div>
  );
}

function useInvalidate(id: string) {
  const qc = useQueryClient();
  return () => Promise.all([
    qc.invalidateQueries({ queryKey: ['receipt', id] }), qc.invalidateQueries({ queryKey: ['receipts'] }),
    qc.invalidateQueries({ queryKey: ['dashboard'] }), qc.invalidateQueries({ queryKey: ['refunds'] }),
    qc.invalidateQueries({ queryKey: ['cancellations'] }), qc.invalidateQueries({ queryKey: ['credit'] }),
    qc.invalidateQueries({ queryKey: ['stats'] }),
  ]);
}

/** Veresiye: ödendi olarak işaretle / geri al. Fiş sahibi ya da yönetici yapar (kapalı aydaki fiş için de). */
function CreditPanel({ r, mine }: { r: ReceiptDetail; mine: boolean }) {
  const { isAdmin } = useAuth();
  const qc = useQueryClient();
  const [paidOn, setPaidOn] = useState(todayTR());
  const refresh = () => Promise.all([qc.invalidateQueries({ queryKey: ['receipt', r.id] }), qc.invalidateQueries({ queryKey: ['credit'] })]);
  const settle = useMutation({ mutationFn: () => ep.settleCredit(r.id, paidOn), onSuccess: refresh });
  const undo = useMutation({ mutationFn: () => ep.unsettleCredit(r.id), onSuccess: refresh });
  const can = mine || isAdmin;
  return (
    <div className="card stack no-print">
      <h3 style={{ margin: 0 }}>Veresiye ödemesi</h3>
      <ErrorBox error={settle.error ?? undo.error} />
      {r.settlement ? (
        <>
          <p style={{ margin: 0 }}>{formatDate(r.settlement.paid_on)} tarihinde ödendi · işaretleyen: {r.settlement.actor_name}</p>
          {can && <button className="btn small" disabled={undo.isPending}
            onClick={() => { if (confirm('Ödeme kaydı geri alınsın mı? Hatırlatmalar yeniden başlar.')) undo.mutate(); }}>Ödemeyi geri al</button>}
        </>
      ) : (
        <>
          <p className="small muted" style={{ margin: 0 }}>
            Son ödeme tarihi {formatDate(r.payment_due_date)}. Ödeme gününden 1 hafta, 3 gün önce ve ödeme günü herkese hatırlatma gider.
          </p>
          {can ? (
            <div className="row">
              <input type="date" value={paidOn} max={todayTR()} onChange={(e) => setPaidOn(e.target.value)} aria-label="Ödeme tarihi" style={{ maxWidth: 180 }} />
              <button className="btn primary" disabled={settle.isPending || !paidOn} onClick={() => settle.mutate()}>Ödendi olarak işaretle</button>
            </div>
          ) : <p className="small muted" style={{ margin: 0 }}>Ödendi işaretini fişin sahibi ya da yönetici yapar.</p>}
        </>
      )}
    </div>
  );
}

function EditForm({ r, onDone }: { r: ReceiptDetail; onDone: () => void }) {
  const invalidate = useInvalidate(r.id);
  const [f, setF] = useState({
    amount: apiToInput(r.amount), document_date: r.document_date, category: r.category, description: r.description,
    vendor_name: r.vendor_name ?? '', payment_method: (r.payment_method ?? '') as PaymentMethod | '',
    payment_due_date: r.payment_due_date ?? '', reason: '',
  });
  const m = useMutation({
    mutationFn: () => {
      const amount = parseUserAmount(f.amount);
      if (!amount) throw new Error('Tutarı 1.250,50 biçiminde girin.');
      const changes: Record<string, unknown> = {};
      if (amount !== r.amount) changes.amount = amount;
      if (f.document_date !== r.document_date) changes.document_date = f.document_date;
      if (f.category !== r.category) changes.category = f.category;
      if (f.description !== r.description) changes.description = f.description;
      if (f.vendor_name !== (r.vendor_name ?? '')) changes.vendor_name = f.vendor_name;
      if (f.payment_method && f.payment_method !== r.payment_method) changes.payment_method = f.payment_method;
      const due = f.payment_method === 'CREDIT_SALE' ? f.payment_due_date || null : null;
      if (f.payment_method === 'CREDIT_SALE' && !due) throw new Error('Veresiye için ödeme tarihini girin.');
      if (due !== r.payment_due_date && (f.payment_method || r.payment_method)) changes.payment_due_date = due;
      return ep.reviseReceipt(r.id, r.current_revision, f.reason, changes);
    },
    onSuccess: async () => { await invalidate(); onDone(); },
  });
  const submit = (e: FormEvent) => { e.preventDefault(); m.mutate(); };
  return (
    <form className="card stack" onSubmit={submit}>
      <h3>Fişi düzelt</h3>
      <ErrorBox error={m.error} />
      <div className="form-grid two">
        <Field label="Tutar (TL)"><input inputMode="decimal" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} disabled={r.refunds.length > 0} /></Field>
        <Field label="Belge tarihi"><input type="date" max={todayTR()} value={f.document_date} onChange={(e) => setF({ ...f, document_date: e.target.value })} disabled={r.refunds.length > 0} /></Field>
        <Field label="Kategori"><input value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} required /></Field>
        <Field label="Açıklama"><input value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} required /></Field>
        <Field label="Satıcı"><input value={f.vendor_name} onChange={(e) => setF({ ...f, vendor_name: e.target.value })} /></Field>
        <Field label="Ödeme şekli">
          <select value={f.payment_method} onChange={(e) => setF({ ...f, payment_method: e.target.value as PaymentMethod })}>
            <option value="" disabled>Seçin…</option>
            {(Object.keys(PAYMENT_LABELS) as PaymentMethod[]).map((k) => <option key={k} value={k}>{PAYMENT_LABELS[k]}</option>)}
          </select>
        </Field>
        {f.payment_method === 'CREDIT_SALE' && (
          <Field label="Veresiye ödeme tarihi">
            <input type="date" min={f.document_date} value={f.payment_due_date} onChange={(e) => setF({ ...f, payment_due_date: e.target.value })} required />
          </Field>
        )}
      </div>
      {r.refunds.length > 0 && <p className="small muted">İade yapılmış fişin tutarı ve tarihi değiştirilemez.</p>}
      <Field label="Gerekçe (zorunlu)"><input value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} required minLength={3} /></Field>
      <div className="row"><button type="button" className="btn" onClick={onDone}>Vazgeç</button><button className="btn primary" disabled={m.isPending}>Kaydet</button></div>
    </form>
  );
}

function CancelForm({ r, onDone }: { r: ReceiptDetail; onDone: () => void }) {
  const invalidate = useInvalidate(r.id);
  const [reason, setReason] = useState('');
  const m = useMutation({ mutationFn: () => ep.cancelReceipt(r.id, reason), onSuccess: async () => { await invalidate(); onDone(); } });
  return (
    <form className="card stack" onSubmit={(e) => { e.preventDefault(); m.mutate(); }}>
      <h3>Fişi iptal et</h3>
      <p className="small">İptal silinmez; fişin kalan net katkısı sıfırlanır ve İptaller sekmesinde görünür. Bu işlem geri alınamaz.</p>
      <ErrorBox error={m.error} />
      <Field label="Gerekçe (zorunlu)"><input value={reason} onChange={(e) => setReason(e.target.value)} required minLength={3} /></Field>
      <div className="row"><button type="button" className="btn" onClick={onDone}>Vazgeç</button><button className="btn danger" disabled={m.isPending}>İptal et</button></div>
    </form>
  );
}

function RefundForm({ r, remaining, onDone }: { r: ReceiptDetail; remaining: bigint; onDone: () => void }) {
  const invalidate = useInvalidate(r.id);
  const [f, setF] = useState({ amount: formatTL(remaining, false), refund_date: todayTR(), reason: '' });
  const m = useMutation({
    mutationFn: () => {
      const amount = parseUserAmount(f.amount);
      if (!amount) throw new Error('Tutarı 1.250,50 biçiminde girin.');
      if (apiToKurus(amount) > remaining) throw new Error(`İade, kalan ${formatTL(remaining)} tutarını aşamaz.`);
      return ep.refundReceipt(r.id, { amount, refund_date: f.refund_date, reason: f.reason });
    },
    onSuccess: async () => { await invalidate(); onDone(); },
  });
  return (
    <form className="card stack" onSubmit={(e) => { e.preventDefault(); m.mutate(); }}>
      <h3>İade ekle</h3>
      <p className="small">Kalan iade edilebilir tutar: <strong>{formatTL(remaining)}</strong>. İade, gerçekleştiği açık aya eksi hareket olarak yazılır; kaynak ayın rakamları değişmez.</p>
      <ErrorBox error={m.error} />
      <div className="form-grid two">
        <Field label="İade tutarı (TL)"><input inputMode="decimal" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} required /></Field>
        <Field label="İade tarihi"><input type="date" min={r.document_date} max={todayTR()} value={f.refund_date} onChange={(e) => setF({ ...f, refund_date: e.target.value })} required /></Field>
      </div>
      <Field label="Gerekçe (zorunlu)"><input value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} required minLength={3} /></Field>
      <div className="row"><button type="button" className="btn" onClick={onDone}>Vazgeç</button><button className="btn primary" disabled={m.isPending}>İadeyi kaydet</button></div>
      <p className="small muted">Kanonik tutar: {parseUserAmount(f.amount) ? formatTL(parseUserAmount(f.amount)!) : '—'} · Kalan: {kurusToApi(remaining)}</p>
    </form>
  );
}
